"use client"

type XRSessionMode = "immersive-ar" | "immersive-vr"

type XRSystemLike = {
  isSessionSupported?: (mode: XRSessionMode) => Promise<boolean>
  addEventListener?: (type: "devicechange", listener: () => void) => void
  removeEventListener?: (type: "devicechange", listener: () => void) => void
}

type NavigatorWithXR = Navigator & {
  xr?: XRSystemLike
}

export function getXRSystem(): XRSystemLike | null {
  if (typeof navigator === "undefined") return null
  return (navigator as NavigatorWithXR).xr ?? null
}

export function canAttemptImmersiveVR(): boolean {
  if (typeof window === "undefined") return false
  return window.isSecureContext && !!getXRSystem()
}

export function canAttemptImmersiveXR(): boolean {
  return canAttemptImmersiveVR()
}

export async function checkImmersiveVRSupport(): Promise<boolean> {
  if (!canAttemptImmersiveVR()) return false

  const xr = getXRSystem()
  if (!xr?.isSessionSupported) return false

  try {
    return await xr.isSessionSupported("immersive-vr")
  } catch {
    return false
  }
}

export async function checkImmersiveARSupport(): Promise<boolean> {
  if (!canAttemptImmersiveXR()) return false

  const xr = getXRSystem()
  if (!xr?.isSessionSupported) return false

  try {
    return await xr.isSessionSupported("immersive-ar")
  } catch {
    return false
  }
}

export function onXRDeviceChange(listener: () => void): () => void {
  const xr = getXRSystem()
  xr?.addEventListener?.("devicechange", listener)
  return () => xr?.removeEventListener?.("devicechange", listener)
}
